import { FC, ReactElement, ReactNode, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import { getAuth } from 'firebase/auth';

import {
	EmailResentSuccessModal,
	MplButton,
	VerificationAnim,
} from '@mopla/ui';
import { FormTitle } from '@mopla/ui/layouts/WelcomeScreen';
import { usePersistentCountdownGuard } from '@mopla/utils';

import { Bold } from './RegistrationForm.styles';

interface VerifyEmailSentViewProps {
	email: string;
	onResend(): void | Promise<void>;
	passwordless?: boolean;
	render: (p: { content: ReactNode; submit: ReactNode }) => ReactElement;
}

export const VerifyEmailSentView: FC<VerifyEmailSentViewProps> = (props) => {
	const { email, onResend, passwordless = false, render } = props;
	const auth = getAuth();
	const { t } = useTranslation(['registration', 'logIn']);
	const [showResentSuccess, setShowResentSuccess] = useState(false);
	const { countdownGuardValue, reinitCountdownGuard } =
		usePersistentCountdownGuard('emailSendTime');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const resendSubmitHandler = useCallback(async () => {
		setIsSubmitting(true);

		await onResend();

		reinitCountdownGuard();
		setIsSubmitting(false);
		setShowResentSuccess(true);
	}, [onResend, reinitCountdownGuard]);

	const mainText = passwordless
		? 'logIn:passwordless.text.email_sent'
		: 'registration:complete.text.title';
	const additionalText = passwordless
		? 'logIn:passwordless.text.email_sent_description'
		: 'registration:complete.text.description';

	return render({
		content: (
			<Stack className="content">
				<VerificationAnim />
				<FormTitle testId="registrationForm-verify-email-screen">
					<Trans
						i18nKey={mainText}
						values={{ regEmail: email || auth?.currentUser?.email }}
						components={{ bold: <Bold /> }}
					/>
				</FormTitle>
				<Typography
					variant="body2"
					marginTop={3}
					color="solutions.600"
					textAlign="center"
				>
					{t(additionalText)}
				</Typography>
				<EmailResentSuccessModal
					open={showResentSuccess}
					onClose={() => setShowResentSuccess(false)}
					email={email || auth?.currentUser?.email}
				/>
			</Stack>
		),
		submit: (
			<MplButton
				fullWidth
				variant="outlined"
				color="primaryButtonV2"
				onClick={resendSubmitHandler}
				disabled={!!countdownGuardValue}
				data-testid="registrationForm-verify-email-resend-button"
				showLoader={isSubmitting}
			>
				{t('complete.text.send_again', {
					context: countdownGuardValue ? 'wait' : undefined,
					time: countdownGuardValue,
				})}
			</MplButton>
		),
	});
};
